import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';

import { Dialog } from '@shared/ui/dialog';

import { copy } from './copy';
import { personSchema, type PersonFields, type PersonValues } from './forms.schema';

/**
 * Create a credited person.
 *
 * **Create is a dialog and edit is a page**, and the split comes from the route
 * map rather than from preference: `/people/:slug` exists, so editing has
 * somewhere to go and the name in the row is that link; there is no
 * `/people/new`, so creating has nowhere to go.
 *
 * A native `<dialog>` opened with `showModal()` — the focus trap, the backdrop,
 * `Esc`, and focus return to the trigger are all the browser's.
 *
 * **«ألغِ» carries `formnovalidate`.** Both buttons in the form are submits,
 * and a submit runs constraint validation — so without it the browser blocks
 * the *exit* and demands a value from an operator who has just decided not to
 * supply one, leaving a dialog whose only ways out are `Esc` and the backdrop.
 */
export function PersonDialog({
  open,
  busy,
  onCancel,
  onCommit,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onCommit: (values: PersonValues) => void;
}) {
  const headingId = useId();
  const nameId = useId();
  const biographyId = useId();
  const biographyHintId = useId();

  const form = useForm<PersonFields, unknown, PersonValues>({
    resolver: zodResolver(personSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: { displayName: '', biography: '' },
  });

  // A dialog re-opened after a cancel must not still hold what was typed into
  // the record the operator decided not to create.
  useEffect(() => {
    if (open) form.reset({ displayName: '', biography: '' });
  }, [open, form]);

  return (
    <Dialog open={open} labelledBy={headingId} onClose={onCancel}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit(onCommit)(event);
        }}
      >
        <h2 id={headingId} className="mb-2 font-display text-h3 font-semibold">
          {copy.dialog.heading}
        </h2>
        <p className="mb-4 max-w-none text-xs text-ink-muted">{copy.dialog.lede}</p>

        <p className="mb-4 max-w-none text-xs text-ink-muted">
          {copy.dialog.requiredNote}
          <span className="ms-[2px] text-danger">*</span>
          {copy.dialog.requiredNoteTail}
        </p>

        <div className="mb-5">
          <label htmlFor={nameId} className="mb-2 block text-sm font-medium">
            {copy.dialog.name.label}
            <span className="ms-[2px] text-danger" aria-hidden="true">
              *
            </span>
          </label>
          <input
            {...form.register('displayName')}
            id={nameId}
            type="text"
            required
            aria-invalid={form.formState.errors.displayName ? true : undefined}
            className="min-h-[2.75rem] w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [line-height:normal] hover:border-ink-muted focus-visible:border-accent"
          />
          {form.formState.errors.displayName?.message ? (
            <p className="mb-0 mt-2 text-xs text-danger">{form.formState.errors.displayName.message}</p>
          ) : null}
        </div>

        <div className="mb-5">
          <label htmlFor={biographyId} className="mb-2 block text-sm font-medium">
            {copy.dialog.biography.label}
          </label>
          <textarea
            {...form.register('biography')}
            id={biographyId}
            rows={4}
            aria-describedby={biographyHintId}
            className="w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [resize:vertical] hover:border-ink-muted focus-visible:border-accent"
          />
          <p id={biographyHintId} className="mb-0 mt-2 text-xs text-ink-muted">
            {copy.dialog.biography.hint}
          </p>
        </div>

        {/* The commit sits at the reading-start edge, the way out beside it. */}
        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={busy}
            className="hit-target rounded-sm border border-accent bg-accent px-5 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.dialog.commit}
          </button>
          <button
            type="button"
            formNoValidate
            onClick={onCancel}
            className="hit-target rounded-sm px-4 text-sm text-ink-muted hover:bg-hover-bg hover:text-ink"
          >
            {copy.dialog.cancel}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
